import { useState } from 'react';
import {
  IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonContent,
  IonList, IonItem, IonLabel, IonInput, IonNote, IonText, IonIcon,
} from '@ionic/react';
import { checkmarkCircle, createOutline, arrowForwardOutline } from 'ionicons/icons';
import { diagnoseRecipeCost, computeDishEconomics, type Recipe, type RecipeCostUnavailableReason } from '@trackeat/shared';
import { useEnqueueEvent, useEnqueueMaster } from '../../lib/data/queries.js';

// T-300 (Food Cost): pannello FOCALIZZATO per sistemare ciò che blocca il food
// cost di UNA ricetta, scrivendo il dato DUREVOLE (così resta nel tempo):
//  · costo ignoto → evento append-only `cost_adjustment` (foods è il read model);
//  · quantità mancante → `recipes.ingredients[].qty`;
//  · unità ricetta errata → allinea la riga alla UdM stock canonica. La UdM
//    del food non si modifica qui: con giacenza richiede il rebase atomico del
//    magazzino, che riproporziona anche quantità e costo.
// Niente editor dispersivo: solo i problemi, ognuno con un fix inline.

type FoodRow = {
  id: string;
  name?: string;
  default_uom?: string | null;
  last_unit_cost?: number | null;
  last_unit_cost_uom?: string | null;
  last_unit_cost_currency?: string | null;
  category?: string | null;
  version: number;
};
const eur = (n: number) => `€ ${n.toFixed(2)}`;
// Quantità senza rumore di virgola mobile (0.16 → "0.16", 1 → "1").
const fmtQty = (n: number | null): string => (n == null ? '—' : String(Number(n.toFixed(3))));

interface Props {
  recipeId: string | null;
  foods: FoodRow[];
  recipesById: Map<string, Recipe>;
  foodUnitCost: (id: string) => number | null;
  foodUom: (id: string) => string | null | undefined;
  // T-450 F1: aliquota IVA di default del locale (%), per lo scorporo nel blocco
  // prezzo & margine della Scheda di costo (override per ricetta = recipe.vat_rate).
  defaultVatRate: number;
  onClose: () => void;
  // T-342: apri l'editor ricetta completo (per correggere ingredienti/resa, non
  // solo i dati mancanti del costo).
  onEditFull?: (id: string) => void;
  // T-342: ri-punta questo stesso modalino a un semilavorato che blocca il costo.
  onOpenRecipe?: (id: string) => void;
}

export function RecipeCostFixModal({ recipeId, foods, recipesById, foodUnitCost, foodUom, defaultVatRate, onClose, onEditFull, onOpenRecipe }: Props): JSX.Element {
  const enqueueEvent = useEnqueueEvent();
  const enqueueMaster = useEnqueueMaster();
  const [busy, setBusy] = useState<string | null>(null);
  const [costDraft, setCostDraft] = useState<Record<string, string>>({});
  const [qtyDraft, setQtyDraft] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<string | null>(null);

  const recipe = recipeId ? recipesById.get(recipeId) ?? null : null;
  const foodById = new Map(foods.map((f) => [f.id, f]));
  const foodName = (id: string) => foodById.get(id)?.name ?? id.slice(0, 8);
  const foodUomOf = (id: string) => foodById.get(id)?.default_uom ?? '—';

  async function patchIngredient(foodId: string, change: { qty?: number; uom?: string }): Promise<void> {
    if (!recipe) return;
    const next = (recipe.ingredients ?? []).map((i) =>
      i.food_id === foodId
        ? { ...i, ...(change.qty !== undefined ? { qty: change.qty } : {}), ...(change.uom !== undefined ? { uom: change.uom } : {}) }
        : i,
    );
    setBusy(foodId);
    try {
      await enqueueMaster.mutateAsync({ op: 'update', table: 'recipes', id: recipe.id, patch: { ingredients: next }, base_version: recipe.version });
      setToast('Salvato — ricalcolo food cost…');
    } catch { setToast('Errore nel salvataggio'); } finally { setBusy(null); }
  }

  async function appendCostAdjustment(
    foodId: string,
    unitCost: number,
    costUom: string,
  ): Promise<void> {
    setBusy(foodId);
    try {
      const now = new Date().toISOString();
      await enqueueEvent.mutateAsync({
        kind: 'cost_adjustment',
        payload: {
          food_id: foodId,
          unit_cost: unitCost,
          cost_uom: costUom,
          currency: 'EUR',
          effective_at: now,
          reason: 'recipe_cost_missing',
        },
        clientTs: now,
      });
      setToast('Salvato — ricalcolo food cost…');
    } catch {
      setToast('Errore nel salvataggio');
    } finally {
      setBusy(null);
    }
  }

  const diag = recipe ? diagnoseRecipeCost(recipe, { recipesById, foodUnitCost, foodUom }) : null;
  // T-442: valorizzazione riga-per-riga, ordinata dal costo più alto (i costi che
  // "trainano" il piatto in cima); le righe non risolte finiscono in fondo.
  const orderedLines = diag
    ? [...diag.lines].sort((a, b) => (a.resolved !== b.resolved ? (a.resolved ? -1 : 1) : (b.lineCost ?? 0) - (a.lineCost ?? 0)))
    : [];
  // T-458: split della composizione per tipologia — packaging/monouso (Nivapack &
  // simili) separati dagli ingredienti/semilavorati, così si vede quanto pesa il
  // confezionamento sul costo del piatto. I semilavorati (sub) restano nel gruppo
  // "cibo". Se non ci sono righe packaging, la lista resta unica (nessun cambio).
  const NON_FOOD_CATS = new Set(['packaging', 'detersivi']);
  type CostLine = (typeof orderedLines)[number];
  const isPackagingLine = (l: CostLine): boolean =>
    l.kind === 'ingredient' && NON_FOOD_CATS.has(foodById.get(l.at)?.category ?? '');
  const foodLines = orderedLines.filter((l) => !isPackagingLine(l));
  const packLines = orderedLines.filter(isPackagingLine);
  const groupSubtotal = (ls: CostLine[]): number =>
    ls.reduce((s, l) => s + (l.resolved && l.lineCost != null ? l.lineCost : 0), 0);
  // T-450 F1: blocco prezzo & margine della Scheda di costo — scorporo IVA dal
  // prezzo di vendita (lordo a menù) e food cost %/margine sul NETTO imponibile,
  // coerente col costo alimento. `costPerPortion` senza fallback (mai numero
  // falsato quando mancano le porzioni).
  const vatRate = recipe?.vat_rate ?? defaultVatRate;
  const econ = recipe
    ? computeDishEconomics({
        costPerPortion: diag?.costPerPortion ?? null,
        sellingPriceCentsGross: recipe.selling_price_cents ?? null,
        vatRatePct: vatRate,
      })
    : null;

  // T-458: stili gruppo + render di UNA riga di composizione, riusato dai due
  // gruppi (cibo / packaging) così il codice della riga resta uno solo.
  const gHeaderStyle = { padding: '6px 12px', background: '#f1f5f9', fontSize: 11.5, fontWeight: 700, color: '#475569', textTransform: 'uppercase' as const, letterSpacing: 0.3, display: 'flex', alignItems: 'center', gap: 6, borderTop: '1px solid #e2e8f0' };
  const gSubStyle = { display: 'flex', justifyContent: 'space-between', padding: '7px 12px', background: '#fafcff', borderTop: '1px dashed #dbe3ee', fontSize: 12.5 };
  const renderCostRow = (l: CostLine, i: number): JSX.Element => {
    const name = l.kind === 'sub' ? (recipesById.get(l.at)?.name ?? l.at.slice(0, 8)) : foodName(l.at);
    const uom = l.uom ?? (l.kind === 'ingredient' ? foodUomOf(l.at) : '—');
    const costUom = l.converted?.uom ?? uom;
    const pct = l.resolved && l.lineCost != null && diag && diag.partialCost > 0
      ? Math.round((l.lineCost / diag.partialCost) * 100) : null;
    return (
      <div key={`${l.at}:${i}`} style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
        padding: '8px 12px', borderTop: i === 0 ? 'none' : '1px solid #f1f5f9',
        background: l.resolved ? '#fff' : '#fef9f3',
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: l.resolved ? '#0f172a' : '#92400e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {name}{l.kind === 'sub' && <span style={{ fontWeight: 400, fontSize: 11, color: '#94a3b8' }}> · semilavorato</span>}
          </div>
          {l.resolved ? (
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {fmtQty(l.qty)} {uom}
              {l.converted && <span style={{ color: '#0369a1' }}> → {fmtQty(l.converted.qty)} {l.converted.uom}</span>}
              {' '}× {eur(l.unitCost ?? 0)}/{costUom} = {eur(l.lineCost ?? 0)}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>
              {l.qty != null ? `${fmtQty(l.qty)} ${uom} · ` : ''}⚠ {reasonShort(l.reason)}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
          {l.resolved && l.lineCost != null ? (
            <>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{eur(l.lineCost)}</div>
              {pct != null && <div style={{ fontSize: 11, color: '#94a3b8' }}>{pct}%</div>}
            </>
          ) : (
            <div style={{ fontSize: 14, color: '#cbd5e1' }}>—</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <IonModal isOpen={!!recipe} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Sistema food cost</IonTitle>
          <IonButtons slot="end"><IonButton onClick={onClose}>Chiudi</IonButton></IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {!recipe || !diag ? null : (
          <>
            <h2 style={{ fontWeight: 700, marginTop: 0 }}>{recipe.name}</h2>
            <p style={{ color: diag.severity === 'green' ? '#15803d' : diag.severity === 'orange' ? '#b45309' : '#b91c1c', fontSize: 14 }}>
              {diag.severity === 'green'
                ? `✅ Completa — costo ${eur(diag.partialCost)}`
                : `${diag.resolved}/${diag.total} ingredienti valorizzati · costo parziale ${eur(diag.partialCost)}`}
            </p>
            {toast && <IonText color="medium"><p style={{ fontSize: 12 }}>{toast}</p></IonText>}

            {/* T-442: Composizione del costo — valorizzazione di OGNI ingrediente
                (qty × costo unitario = € contribuito, con % sul totale), così si
                vede COME si compone il costo e non solo il numero finale. */}
            {orderedLines.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <h3 style={{ fontWeight: 700, fontSize: 15, margin: '4px 0 8px' }}>Composizione del costo</h3>
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                  {/* T-458: split per tipologia — se ci sono righe packaging, due
                      gruppi con subtotale (cibo / packaging); altrimenti lista unica. */}
                  {packLines.length === 0 ? (
                    foodLines.map((l, i) => renderCostRow(l, i))
                  ) : (
                    <>
                      <div style={gHeaderStyle}>🍽️ Ingredienti &amp; semilavorati</div>
                      {foodLines.map((l, i) => renderCostRow(l, i))}
                      <div style={gSubStyle}><span style={{ color: '#475569' }}>Subtotale cibo</span><span style={{ fontWeight: 700, color: '#0f172a' }}>{eur(groupSubtotal(foodLines))}</span></div>
                      <div style={gHeaderStyle}>📦 Packaging e monouso</div>
                      {packLines.map((l, i) => renderCostRow(l, i))}
                      <div style={gSubStyle}><span style={{ color: '#475569' }}>Subtotale packaging</span><span style={{ fontWeight: 700, color: '#0f172a' }}>{eur(groupSubtotal(packLines))}</span></div>
                    </>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', borderTop: '2px solid #e2e8f0', background: '#f8fafc' }}>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{diag.severity === 'green' ? 'Totale' : 'Totale (parziale)'}</span>
                    <span style={{ fontWeight: 800, fontSize: 15 }}>{eur(diag.partialCost)}</span>
                  </div>
                  {diag.costPerPortion != null && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', background: '#f8fafc', borderTop: '1px solid #eef2f7' }}>
                      <span style={{ fontSize: 12, color: '#64748b' }}>Per porzione</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{eur(diag.costPerPortion)}</span>
                    </div>
                  )}
                </div>
                {diag.severity !== 'green' && (
                  <p style={{ fontSize: 12, color: '#94a3b8', margin: '6px 2px 0' }}>
                    Le righe in arancione non entrano ancora nel totale: sistemale qui sotto.
                  </p>
                )}
              </div>
            )}

            {/* T-450 F1: PREZZO & MARGINE — scorporo IVA passo-passo. Ogni numero
                è visibile così i conti tornano a mano (prezzo lordo → netto →
                food cost % → margine). */}
            {econ && (
              <div style={{ marginTop: 14 }}>
                <h3 style={{ fontWeight: 700, fontSize: 15, margin: '4px 0 8px' }}>Prezzo &amp; margine</h3>
                {recipe.selling_price_cents == null ? (
                  <p style={{ fontSize: 13, color: '#b45309', margin: '0 2px' }}>
                    Imposta il prezzo di vendita (dalla lista Ricette) per calcolare food cost % e margine.
                  </p>
                ) : (
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                    <SchedaRow label={`Prezzo di vendita (IVA ${fmtQty(vatRate)}% inclusa)`} value={eur(econ.sellingGross ?? 0)} />
                    <SchedaRow
                      label={`− scorporo IVA (÷ ${fmtQty(1 + vatRate / 100)}) → prezzo netto`}
                      value={econ.sellingNet != null ? eur(econ.sellingNet) : '—'}
                    />
                    {diag.costPerPortion == null ? (
                      <div style={{ padding: '8px 12px', fontSize: 12.5, color: '#b45309', borderTop: '1px solid #f1f5f9', background: '#fffbeb' }}>
                        Imposta il numero di porzioni per food cost % e margine.
                      </div>
                    ) : (
                      <>
                        <SchedaRow
                          label={`Food cost % = ${eur(diag.costPerPortion)} ÷ ${eur(econ.sellingNet ?? 0)} × 100`}
                          value={econ.foodCostPct != null ? `${econ.foodCostPct.toFixed(1)} %` : '—'}
                          strong
                          valueColor={econ.foodCostPct != null && econ.foodCostPct > 35 ? '#b91c1c' : '#15803d'}
                        />
                        <SchedaRow
                          label={`Margine (netto IVA) = ${eur(econ.sellingNet ?? 0)} − ${eur(diag.costPerPortion)}`}
                          value={econ.marginNet != null ? eur(econ.marginNet) : '—'}
                          strong
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {diag.missing.length === 0 ? (
              <IonText color="success"><p><IonIcon icon={checkmarkCircle} /> Tutto valorizzato, niente da sistemare.</p></IonText>
            ) : (
              <IonList>
                <h3 style={{ fontWeight: 700, fontSize: 15, margin: '14px 2px 2px' }}>Da sistemare</h3>
                {diag.missing.map((m, idx) => {
                  const id = m.at;
                  const key = `${m.reason}:${id}:${idx}`;
                  const disabled = busy === id;
                  // Costo del prodotto: fatto append-only; foods è solo il
                  // read model server-side ricostruito da acquisti + rettifiche.
                  if (m.reason === 'ingredient_cost_unknown') {
                    return (
                      <IonItem key={key}>
                        <IonLabel className="ion-text-wrap">
                          <h3 style={{ fontWeight: 600 }}>🔴 {foodName(id)}</h3>
                          <IonNote>Costo di carico mancante</IonNote>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                            <IonInput type="number" inputmode="decimal" placeholder="0.00" style={{ maxWidth: 110, border: '1px solid #cbd5e1', borderRadius: 8, paddingLeft: 8 }}
                              value={costDraft[id] ?? ''} onIonInput={(e) => setCostDraft((d) => ({ ...d, [id]: String(e.detail.value ?? '') }))} />
                            <span style={{ fontSize: 13, color: '#64748b' }}>€ / {foodUomOf(id)}</span>
                            <IonButton size="small" disabled={disabled} onClick={() => {
                              const v = Number((costDraft[id] ?? '').replace(',', '.'));
                              const uom = foodById.get(id)?.default_uom?.trim();
                              if (Number.isFinite(v) && v >= 0 && uom) {
                                void appendCostAdjustment(id, v, uom);
                              }
                            }}>Salva</IonButton>
                          </div>
                        </IonLabel>
                      </IonItem>
                    );
                  }
                  // Quantità in ricetta (durevole: recipes.ingredients[].qty)
                  if (m.reason === 'ingredient_qty_missing') {
                    return (
                      <IonItem key={key}>
                        <IonLabel className="ion-text-wrap">
                          <h3 style={{ fontWeight: 600 }}>🔴 {foodName(id)}</h3>
                          <IonNote>Quantità in ricetta mancante</IonNote>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                            <IonInput type="number" inputmode="decimal" placeholder="0" style={{ maxWidth: 110, border: '1px solid #cbd5e1', borderRadius: 8, paddingLeft: 8 }}
                              value={qtyDraft[id] ?? ''} onIonInput={(e) => setQtyDraft((d) => ({ ...d, [id]: String(e.detail.value ?? '') }))} />
                            <span style={{ fontSize: 13, color: '#64748b' }}>{foodUomOf(id)}</span>
                            <IonButton size="small" disabled={disabled} onClick={() => {
                              const v = Number((qtyDraft[id] ?? '').replace(',', '.'));
                              if (Number.isFinite(v) && v > 0) void patchIngredient(id, { qty: v });
                            }}>Salva</IonButton>
                          </div>
                        </IonLabel>
                      </IonItem>
                    );
                  }
                  // L'unità stock è canonica: qui correggiamo esclusivamente
                  // la riga ricetta. Cambiare il food richiede il rebase
                  // atomico dalla Giacenza.
                  if (m.reason === 'ingredient_uom_mismatch') {
                    const recIng = (recipe.ingredients ?? []).find((i) => i.food_id === id);
                    const canonicalUom = foodById.get(id)?.default_uom?.trim() || null;
                    return (
                      <IonItem key={key}>
                        <IonLabel className="ion-text-wrap">
                          <h3 style={{ fontWeight: 600 }}>🟠 {foodName(id)}</h3>
                          <IonNote>Unità ricetta ({recIng?.uom ?? '—'}) ≠ unità di carico ({foodUomOf(id)})</IonNote>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                            <IonButton
                              size="small"
                              fill="outline"
                              disabled={!canonicalUom || busy === id}
                              onClick={() => {
                                if (canonicalUom) void patchIngredient(id, { uom: canonicalUom });
                              }}
                            >
                              Allinea ricetta a {canonicalUom ?? 'UdM stock'}
                            </IonButton>
                            <span style={{ fontSize: 12, color: '#94a3b8' }}>
                              Se è errata la UdM stock, usa “Correggi unità” in Giacenza.
                            </span>
                          </div>
                        </IonLabel>
                      </IonItem>
                    );
                  }
                  // Semilavorati: T-342 — apri il semilavorato con un tap (prima
                  // era solo testo, vicolo cieco). Se il semilavorato esiste,
                  // ri-punta questo modalino ad esso; altrimenti niente bottone.
                  const subExists = recipesById.has(id);
                  return (
                    <IonItem key={key}>
                      <IonLabel className="ion-text-wrap">
                        <h3 style={{ fontWeight: 600 }}>🟠 Semilavorato</h3>
                        <IonNote>{labelFor(m.reason)}{subExists ? ` — "${recipesById.get(id)?.name ?? id.slice(0, 8)}"` : ''}</IonNote>
                      </IonLabel>
                      {subExists && onOpenRecipe && (
                        <IonButton slot="end" size="small" fill="outline" onClick={() => onOpenRecipe(id)}>
                          Apri <IonIcon slot="end" icon={arrowForwardOutline} />
                        </IonButton>
                      )}
                    </IonItem>
                  );
                })}
              </IonList>
            )}
            {/* T-342: per correggere ciò che NON è "mancante" (un ingrediente
                sbagliato/dimenticato, la resa, ecc.) si apre l'editor completo. */}
            {onEditFull && (
              <IonButton expand="block" fill="outline" style={{ marginTop: 16 }} onClick={() => onEditFull(recipe.id)}>
                <IonIcon slot="start" icon={createOutline} /> Modifica ricetta completa
              </IonButton>
            )}
          </>
        )}
      </IonContent>
    </IonModal>
  );
}

// T-450 F1: riga "label = valore" della Scheda di costo (blocco prezzo & margine),
// con la formula esplicita a sinistra e il risultato a destra.
function SchedaRow({ label, value, strong, valueColor }: { label: string; value: string; strong?: boolean; valueColor?: string }): JSX.Element {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
      padding: '8px 12px', borderTop: '1px solid #f1f5f9',
    }}>
      <span style={{ fontSize: 12.5, color: '#475569', minWidth: 0 }}>{label}</span>
      <span style={{ fontSize: strong ? 15 : 13, fontWeight: strong ? 800 : 600, color: valueColor ?? '#0f172a', flex: '0 0 auto' }}>{value}</span>
    </div>
  );
}

// T-442: etichetta breve inline per la riga non risolta della "Composizione del
// costo" (l'azione per correggerla è nella lista "Da sistemare" più sotto).
function reasonShort(reason?: RecipeCostUnavailableReason): string {
  switch (reason) {
    case 'ingredient_cost_unknown': return 'costo mancante';
    case 'ingredient_qty_missing': return 'quantità mancante';
    case 'ingredient_uom_mismatch': return 'unità non coerente';
    case 'no_cost_inputs': return 'nessun ingrediente';
    default: return reason ? labelFor(reason).toLowerCase() : 'non disponibile';
  }
}

function labelFor(reason: RecipeCostUnavailableReason): string {
  switch (reason) {
    case 'sub_recipe_not_found': return 'Semilavorato non trovato';
    case 'sub_recipe_qty_unknown': return 'Quantità semilavorato mancante';
    case 'sub_recipe_uom_mismatch': return 'Unità semilavorato ≠ resa';
    case 'sub_recipe_cycle': return 'Ricetta ciclica';
    default: return 'Costo semilavorato non disponibile';
  }
}
